import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

function severityColor(severity) {
    if (severity === 'error') return 'bg-red-900/70 border-red-700 text-red-100';
    if (severity === 'warning') return 'bg-amber-900/70 border-amber-700 text-amber-100';
    return 'bg-blue-900/60 border-blue-700 text-blue-50';
}

export default function AlertBanner({ alerts, counts, emailStatus, onAcknowledge, adminEnabled }) {
    const [expanded, setExpanded] = useState(false);
    const hasAlerts = Array.isArray(alerts) && alerts.length > 0;
    const alertCount = Array.isArray(alerts) ? alerts.length : 0;
    const summary = useMemo(() => {
        if (!hasAlerts) return 'All clear';
        const warn = counts?.warning || 0;
        const err = counts?.error || 0;
        const info = counts?.info || 0;
        return `${alertCount} active • ${warn} warnings, ${err} errors${info ? `, ${info} info` : ''}`;
    }, [alertCount, counts, hasAlerts]);

    if (!hasAlerts) return null;

    const dominant = (counts?.error || 0) > 0 ? 'error' : (counts?.warning || 0) > 0 ? 'warning' : 'info';
    const emailSummary = emailStatus
        ? `${emailStatus.enabled ? 'Email on' : 'Email off'}${emailStatus.dry_run ? ' (dry-run)' : ''}`
        : 'Email unknown';

    return (
        <div className={`border rounded-xl px-4 py-3 mb-4 ${severityColor(dominant)}`} data-testid="alert-banner">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <p className="text-xs uppercase tracking-wide">Alerts</p>
                    <p className="text-sm font-semibold">{summary}</p>
                    <p className="text-[0.7rem] opacity-80" data-testid="email-status">
                        {emailSummary}
                        {emailStatus?.last_error ? ` • error: ${emailStatus.last_error}` : ''}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {adminEnabled && (
                        <button
                            type="button"
                            onClick={onAcknowledge}
                            className="text-xs px-3 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10"
                            data-testid="acknowledge-alerts"
                        >
                            Acknowledge all
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                        data-testid="toggle-alert-details"
                    >
                        {expanded ? 'Hide' : 'Details'}
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="mt-3 space-y-2" data-testid="alert-details">
                    {alerts.map((alert) => (
                        <div key={alert.code} className="text-xs border-t border-white/10 pt-2" data-testid="alert-row">
                            <div className="flex items-center justify-between">
                                <span className="font-semibold">[{alert.severity}] {alert.code}</span>
                                <span className="text-[0.65rem] opacity-80">first {alert.first_seen} • last {alert.last_seen}</span>
                            </div>
                            <p className="text-sm">{alert.message}</p>
                            <p className="text-[0.65rem] opacity-75" data-testid="alert-classification">
                                {alert.details?.message_key || alert.code}
                                {alert.details?.category ? ` • ${alert.details.category}` : ''}
                                {alert.details?.module ? ` • ${alert.details.module}` : ''}
                                {alert.details?.readiness ? ` • ${alert.details.readiness}` : ''}
                                {alert.source ? ` • ${alert.source}` : ''}
                            </p>
                            {alert.details?.last_error && (
                                <p className="text-[0.7rem] opacity-80">{alert.details.last_error}</p>
                            )}
                        </div>
                    ))}
                    {emailStatus && (
                        <div className="text-xs border-t border-white/10 pt-2" data-testid="email-row">
                            <div className="flex items-center justify-between">
                                <span className="font-semibold">Email status</span>
                                <span className="text-[0.65rem] opacity-80">
                                    {emailStatus.last_send_ts ? `last send ${emailStatus.last_send_ts}` : 'no send yet'}
                                </span>
                            </div>
                            <p className="text-sm">
                                {emailStatus.enabled ? 'Enabled' : 'Disabled'}
                                {emailStatus.dry_run ? ' • dry-run' : ''}
                                {emailStatus.disable_reason ? ` • ${emailStatus.disable_reason}` : ''}
                            </p>
                            {emailStatus.last_error && <p className="text-[0.7rem] opacity-80">{emailStatus.last_error}</p>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

AlertBanner.propTypes = {
    alerts: PropTypes.arrayOf(PropTypes.object),
    counts: PropTypes.object,
    emailStatus: PropTypes.object,
    onAcknowledge: PropTypes.func,
    adminEnabled: PropTypes.bool,
};
